import React, {useEffect, useState} from 'react'
import {
    Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Divider, FormControlLabel, Grid, IconButton,
    Paper, Switch, TextField, Tooltip
} from '@mui/material'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import LockIcon from '@mui/icons-material/Lock'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import SyncIcon from '@mui/icons-material/Sync'
import {LoaderFunctionArgs, useLoaderData, useNavigate} from 'react-router-dom'
import {DataGrid, GridColDef} from '@mui/x-data-grid'
import axios from 'axios'
import {EventTpSummary} from './TreasurePolicy'

interface TpItem {
    id: number
    name: string
    type: string
    tp_value: number
}

interface EventTpItem {
    id: number
    itemId: number
    item: TpItem
    quantity: number
}

interface MagicItemOption {
    id: number
    item_id: string
    name: string
    tp_value?: number | null
}

interface LoaderData {
    event: EventTpSummary
    items: EventTpItem[]
}

const TYPE_LABELS: Record<string, string> = {
    COIN: 'Coin',
    ADVENTURE_EQUIPMENT: 'Adventure Equipment',
    CRAFTING_MATERIAL: 'Crafting Material',
    REAGENT: 'Reagent',
    SUPERIOR_EQUIPMENT: 'Superior Equipment',
    RITUAL_SCROLL: 'Ritual Scroll',
    CATALYST: 'Catalyst',
}

export const treasurePolicyEventLoader = async ({params}: LoaderFunctionArgs): Promise<LoaderData> => {
    const id = parseInt(params.id!)
    const [eventsRes, itemsRes] = await Promise.all([
        axios.get('/treasure/events'),
        axios.get(`/treasure/events/${id}/items`),
    ])
    const event = eventsRes.data.find((e: EventTpSummary) => e.id === id) ?? {
        id, name: 'Event', assigned_tp: 0, reclaimed_tp: 0, rolled_over_tp: 0, locked: false
    }
    return {event, items: itemsRes.data}
}

export function TreasurePolicyEvent() {
    const loaded = useLoaderData() as LoaderData
    const navigate = useNavigate()

    const [event, setEvent] = useState<EventTpSummary>(loaded.event)
    const [items, setItems] = useState<EventTpItem[]>(loaded.items)
    const [catalog, setCatalog] = useState<TpItem[]>([])
    const [selectedItem, setSelectedItem] = useState<TpItem | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [adding, setAdding] = useState(false)

    // Magic items
    const [magicItems, setMagicItems] = useState<MagicItemOption[]>([])
    const [allMagicItems, setAllMagicItems] = useState<MagicItemOption[]>([])
    const [selectedMagicItem, setSelectedMagicItem] = useState<MagicItemOption | null>(null)
    const [addingMagic, setAddingMagic] = useState(false)

    // Reclaimed TP
    const [reclaimedInput, setReclaimedInput] = useState(String(loaded.event.reclaimed_tp))
    const [savingReclaimed, setSavingReclaimed] = useState(false)

    // CMA scrape
    const [scraping, setScraping] = useState(false)
    const [scrapeResult, setScrapeResult] = useState<string | null>(null)
    const [togglingCma, setTogglingCma] = useState(false)

    // Lock dialog
    const [lockDialogOpen, setLockDialogOpen] = useState(false)
    const [locking, setLocking] = useState(false)

    // Transfer dialog
    const [transferOpen, setTransferOpen] = useState(false)
    const [allEvents, setAllEvents] = useState<EventTpSummary[]>([])
    const [transferTarget, setTransferTarget] = useState<EventTpSummary | null>(null)
    const [transferring, setTransferring] = useState(false)

    const locked = event.locked

    useEffect(() => {
        axios.get('/treasure/tp-items').then(({data}) => setCatalog(data))
        loadMagicItems()
        axios.get('/magic-items/magic-items').then(({data}) => setAllMagicItems(data))
    }, [])

    async function loadMagicItems() {
        const {data} = await axios.get(`/magic-items/magic-items?eventId=${loaded.event.id}`)
        setMagicItems(data)
    }

    async function refreshEvent() {
        const [eventsRes, itemsRes] = await Promise.all([
            axios.get('/treasure/events'),
            axios.get(`/treasure/events/${event.id}/items`),
        ])
        const updated = eventsRes.data.find((e: EventTpSummary) => e.id === event.id)
        if (updated) {
            setEvent(updated)
            setReclaimedInput(String(updated.reclaimed_tp))
        }
        setItems(itemsRes.data)
    }

    async function refreshMagicItems() {
        const [{data: eventMagic}, {data: all}] = await Promise.all([
            axios.get(`/magic-items/magic-items?eventId=${event.id}`),
            axios.get('/magic-items/magic-items'),
        ])
        setMagicItems(eventMagic)
        setAllMagicItems(all)
    }

    async function runCmaScrape() {
        setScraping(true)
        setScrapeResult(null)
        try {
            const {data} = await axios.post(`/api/events/${event.id}/cma-scrape`)
            const maxTpChanged = data.maxTp != null && data.maxTp !== event.max_tp
            setScrapeResult(`Scrape complete — Max TP: ${data.maxTp ?? 'unchanged'}, Registrations updated: ${data.questionsProcessed}`)
            if (maxTpChanged) await refreshEvent()
        } catch (err: any) {
            setScrapeResult(`Scrape failed: ${err.response?.data?.error ?? err.message}`)
        } finally {
            setScraping(false)
        }
    }

    async function toggleCmaScrape(enabled: boolean) {
        setTogglingCma(true)
        try {
            await axios.patch(`/api/events/${event.id}/cma-scrape-enabled`, {cma_scrape_enabled: enabled})
            setEvent(prev => ({...prev, cma_scrape_enabled: enabled}))
        } finally {
            setTogglingCma(false)
        }
    }

    async function saveReclaimed() {
        const val = Math.max(0, parseInt(reclaimedInput) || 0)
        setSavingReclaimed(true)
        await axios.patch(`/treasure/events/${event.id}/reclaimed-tp`, {reclaimed_tp: val})
        await refreshEvent()
        setSavingReclaimed(false)
    }

    async function confirmLock() {
        setLocking(true)
        await axios.post(`/treasure/events/${event.id}/lock`)
        setLocking(false)
        setLockDialogOpen(false)
        await refreshEvent()
    }

    async function openTransfer() {
        const {data} = await axios.get('/treasure/events')
        setAllEvents(data.filter((e: EventTpSummary) => e.id !== event.id))
        setTransferTarget(null)
        setTransferOpen(true)
    }

    async function confirmTransfer() {
        if (!transferTarget || remainingTp <= 0) return
        setTransferring(true)
        await axios.post(`/treasure/events/${event.id}/transfer`, {
            targetEventId: transferTarget.id,
            amount: remainingTp,
        })
        setTransferring(false)
        setTransferOpen(false)
        setTransferTarget(null)
    }

    async function addItem() {
        if (!selectedItem || quantity < 1) return
        setAdding(true)
        try {
            await axios.post(`/treasure/events/${event.id}/items`, {itemId: selectedItem.id, quantity})
            setSelectedItem(null)
            setQuantity(1)
            await refreshEvent()
        } finally {
            setAdding(false)
        }
    }

    async function removeItem(itemId: number) {
        await axios.delete(`/treasure/events/${event.id}/items/${itemId}`)
        await refreshEvent()
    }

    async function addMagicItem() {
        if (!selectedMagicItem) return
        setAddingMagic(true)
        try {
            await axios.patch(`/magic-items/magic-items/${selectedMagicItem.id}/event`, {eventId: event.id})
            setSelectedMagicItem(null)
            await Promise.all([refreshEvent(), refreshMagicItems()])
        } finally {
            setAddingMagic(false)
        }
    }

    async function removeMagicItem(magicItemId: number) {
        await axios.patch(`/magic-items/magic-items/${magicItemId}/event`, {eventId: null})
        await Promise.all([refreshEvent(), refreshMagicItems()])
    }

    function exportCsv() {
        const sorted = [...items].sort((a, b) => {
            const typeA = TYPE_LABELS[a.item?.type] ?? a.item?.type ?? ''
            const typeB = TYPE_LABELS[b.item?.type] ?? b.item?.type ?? ''
            return typeA.localeCompare(typeB) || (a.item?.name ?? '').localeCompare(b.item?.name ?? '')
        })
        const rows = [
            ['Name', 'Type', 'TP Each', 'Qty', 'Total TP'],
            ...sorted.map(i => [
                i.item?.name ?? '',
                TYPE_LABELS[i.item?.type] ?? i.item?.type ?? '',
                String(i.item?.tp_value ?? 0),
                String(i.quantity),
                String((i.item?.tp_value ?? 0) * i.quantity),
            ]),
        ]
        const csv = rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n')
        const blob = new Blob([csv], {type: 'text/csv'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `tp-items-${event.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    const hasMaxOrRollover = event.max_tp != null || event.rolled_over_tp > 0
    const remainingTp = hasMaxOrRollover
        ? (event.max_tp ?? 0) + event.rolled_over_tp - event.assigned_tp + event.reclaimed_tp
        : 0

    const tpItemColumns: GridColDef[] = [
        {field: 'name', headerName: 'Item', flex: 2, minWidth: 160,
            valueGetter: (params: any) => params.row.item?.name ?? '—'},
        {field: 'type', headerName: 'Type', flex: 1, minWidth: 120,
            valueGetter: (params: any) => TYPE_LABELS[params.row.item?.type] ?? params.row.item?.type ?? '—'},
        {field: 'tp_value', headerName: 'TP Each', width: 90, align: 'center', headerAlign: 'center',
            valueGetter: (params: any) => params.row.item?.tp_value ?? 0},
        {field: 'quantity', headerName: 'Qty', width: 80, align: 'center', headerAlign: 'center',
            editable: !locked, type: 'number'},
        {field: 'line_total', headerName: 'Total TP', width: 90, align: 'center', headerAlign: 'center',
            valueGetter: (params: any) => (params.row.item?.tp_value ?? 0) * (params.row.quantity ?? 0)},
        ...(!locked ? [{field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params: any) => (
                <Tooltip title="Remove"><span>
                    <IconButton size="small" color="error" onClick={() => removeItem(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }] : [])
    ]

    const magicItemColumns: GridColDef[] = [
        {field: 'item_id', headerName: 'Item ID', width: 120},
        {field: 'name', headerName: 'Name', flex: 2, minWidth: 160},
        {field: 'tp_value', headerName: 'TP Value', width: 90, align: 'center', headerAlign: 'center',
            valueGetter: (params: any) => params.row.tp_value ?? '—'},
        ...(!locked ? [{field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params: any) => (
                <Tooltip title="Remove from event"><span>
                    <IconButton size="small" color="error" onClick={() => removeMagicItem(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }] : [])
    ]

    const availableMagicItems = allMagicItems.filter(m => !magicItems.some(em => em.id === m.id))

    return (
        <Paper elevation={2} sx={{p: 2}}>
            {/* Header row */}
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Button variant="text" size="small" onClick={() => navigate('/treasure-policy')}>
                    ← Events
                </Button>
                {locked
                    ? <Chip icon={<LockIcon/>} label="Locked" color="warning" size="small"/>
                    : <Button variant="outlined" color="warning" size="small" startIcon={<LockIcon/>}
                        onClick={() => setLockDialogOpen(true)}>
                        Lock Event
                    </Button>
                }
            </Box>

            <Typography variant="h5" gutterBottom>{event.name}</Typography>

            {/* TP summary */}
            <Grid container spacing={2} alignItems="center" sx={{mb: 2}}>
                <Grid item>
                    <Typography variant="body1"><strong>Max TP:</strong> {event.max_tp ?? '—'}</Typography>
                </Grid>
                {event.rolled_over_tp > 0 && (
                    <Grid item>
                        <Typography variant="body1" color="success.main">
                            <strong>Rolled Over TP:</strong> {event.rolled_over_tp}
                        </Typography>
                    </Grid>
                )}
                <Grid item>
                    <Typography variant="body1"><strong>Assigned TP:</strong> {event.assigned_tp}</Typography>
                </Grid>
                <Grid item sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography variant="body1"><strong>Reclaimed TP:</strong></Typography>
                    {locked
                        ? <Typography variant="body1">{event.reclaimed_tp}</Typography>
                        : <>
                            <TextField
                                size="small"
                                type="number"
                                value={reclaimedInput}
                                inputProps={{min: 0, style: {width: 70}}}
                                onChange={e => setReclaimedInput(e.target.value)}
                            />
                            <Button size="small" variant="outlined" disabled={savingReclaimed} onClick={saveReclaimed}>
                                Save
                            </Button>
                        </>
                    }
                </Grid>
                {hasMaxOrRollover && (
                    <Grid item>
                        <Typography variant="body1" color={remainingTp < 0 ? 'error' : 'inherit'}>
                            <strong>Remaining TP:</strong> {remainingTp}
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {/* ── CMA Scraping ──────────────────────────────────────────── */}
            {(event.cma_event_id != null && event.cma_registration_id != null) && (
                <Paper variant="outlined" sx={{p: 2, mb: 2}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>CMA Scraping</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.cma_scrape_enabled}
                                    disabled={togglingCma}
                                    onChange={e => toggleCmaScrape(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={event.cma_scrape_enabled ? 'Enabled (every 4 hrs)' : 'Disabled'}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SyncIcon/>}
                            disabled={scraping}
                            onClick={runCmaScrape}
                        >
                            {scraping ? 'Scraping…' : 'Run CMA Scrape'}
                        </Button>
                        {scrapeResult && (
                            <Typography variant="body2" color={scrapeResult.startsWith('Scrape failed') ? 'error' : 'success.main'}>
                                {scrapeResult}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            )}

            {/* ── Treasure Policy Items ──────────────────────────────────── */}
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Typography variant="h6">Treasure Policy Items</Typography>
                <Tooltip title="Export to CSV">
                    <span><Button size="small" startIcon={<FileDownloadIcon/>} onClick={exportCsv} disabled={items.length === 0}>Export</Button></span>
                </Tooltip>
            </Box>
            <DataGrid
                rows={items}
                columns={tpItemColumns}
                autoHeight
                density="compact"
                pageSizeOptions={[25, 50]}
                initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                sx={{mb: 2}}
                processRowUpdate={async (newRow, oldRow) => {
                    if (newRow.quantity === oldRow.quantity) return oldRow
                    const qty = Math.max(1, Math.round(newRow.quantity))
                    await axios.post(`/treasure/events/${event.id}/items`, {itemId: newRow.itemId, quantity: qty})
                    await refreshEvent()
                    return {...newRow, quantity: qty}
                }}
                onProcessRowUpdateError={() => {}}
            />
            {!locked && (
                <Paper variant="outlined" sx={{p: 2, mb: 3}}>
                    <Typography variant="subtitle1" gutterBottom>Add TP Item</Typography>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                        <Autocomplete
                            sx={{flex: 2, minWidth: 200}}
                            options={catalog}
                            getOptionLabel={i => `${i.name} (${TYPE_LABELS[i.type] ?? i.type}, ${i.tp_value} TP)`}
                            value={selectedItem}
                            onChange={(_e, v) => setSelectedItem(v)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={params => <TextField {...params} label="Item" size="small"/>}
                        />
                        <TextField sx={{width: 90}} label="Qty" type="number" size="small" value={quantity}
                            inputProps={{min: 1}}
                            onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        />
                        <Button variant="contained" onClick={addItem} disabled={!selectedItem || adding}>
                            {adding ? 'Adding…' : 'Add'}
                        </Button>
                    </Box>
                </Paper>
            )}

            <Divider sx={{my: 2}}/>

            {/* ── Magic Items ────────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>Magic Items</Typography>
            <DataGrid
                rows={magicItems}
                columns={magicItemColumns}
                autoHeight
                density="compact"
                pageSizeOptions={[25, 50]}
                initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                sx={{mb: 2}}
            />
            {!locked && (
                <Paper variant="outlined" sx={{p: 2}}>
                    <Typography variant="subtitle1" gutterBottom>Add Magic Item</Typography>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                        <Autocomplete
                            sx={{flex: 2, minWidth: 240}}
                            options={availableMagicItems}
                            getOptionLabel={m => `${m.item_id} — ${m.name}${m.tp_value != null ? ` (${m.tp_value} TP)` : ''}`}
                            value={selectedMagicItem}
                            onChange={(_e, v) => setSelectedMagicItem(v)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={params => <TextField {...params} label="Magic Item" size="small"/>}
                        />
                        <Button variant="contained" onClick={addMagicItem} disabled={!selectedMagicItem || addingMagic}>
                            {addingMagic ? 'Adding…' : 'Add'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Transfer TP (only when locked and remaining > 0) */}
            {locked && hasMaxOrRollover && remainingTp > 0 && (
                <Box sx={{mt: 3}}>
                    <Button variant="outlined" color="success" onClick={openTransfer}>
                        Transfer Remaining TP ({remainingTp}) →
                    </Button>
                </Box>
            )}

            {/* ── Lock confirmation dialog ─────────────────────────────── */}
            <Dialog open={lockDialogOpen} onClose={() => setLockDialogOpen(false)}>
                <DialogTitle>Lock Event</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Lock <strong>{event.name}</strong>? All editing will be permanently disabled.
                        You will still be able to transfer remaining TP to another event.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLockDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" disabled={locking} onClick={confirmLock}>
                        {locking ? 'Locking…' : 'Lock Event'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Transfer TP dialog ───────────────────────────────────── */}
            <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Transfer Remaining TP</DialogTitle>
                <DialogContent sx={{pt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <DialogContentText>
                        Transfer <strong>{remainingTp} TP</strong> from <strong>{event.name}</strong> to:
                    </DialogContentText>
                    <Autocomplete
                        options={allEvents}
                        getOptionLabel={e => e.name}
                        value={transferTarget}
                        onChange={(_e, v) => setTransferTarget(v)}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={params => <TextField {...params} label="Target Event" size="small"/>}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" disabled={!transferTarget || transferring}
                        onClick={confirmTransfer}>
                        {transferring ? 'Transferring…' : 'Transfer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
